import { useEffect, useMemo, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { lockedBuiltInRowId } from "@shared/constants/locked-rows";
import { PERSONAL_INFORMATION } from "@shared/template-spec/personal-information";
import { screenByKey } from "@shared/interview/chapters";
import { boardCell, boardStep, boardTable, boardTiles } from "@shared/interview/board";
import { useSectionRows, type SectionRow } from "./useSectionRows";
import { saveInBackground } from "./saveFailure";
import { TilePicker, type TileSelection } from "./TilePicker";
import { InterviewRowTable, type RowTableRow } from "./InterviewRowTable";
import { LocationBox, LocationBoxPanel } from "./LocationBox";
import { NoteCard } from "./InterviewCells";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";

/**
 * s223 — GOVERNMENT ID (SPEC: `Main.dc.html`, the `ids` step; D-222.10 part 5).
 *
 * Six tiles and "None of these". Each tile is one row of `personal-information-government-ids`, and the row's
 * identity is its `id_type` — so the screen asks only where each one is kept.
 *
 * ## 🔴 A tile pick UNHIDES the matching locked built-in row; un-picking HIDES it (ruled)
 * Four of the six are the section's locked built-in rows (`__builtin__…`), which carry the `_govForms` bindings.
 * Hiding one is new behaviour for those rows: `isRowHidden` exempted every locked row, and s223 opened that
 * exemption for THIS section only (`HIDEABLE_LOCKED_SECTIONS`, `shared/constants/locked-rows.ts`). The two
 * ruled-in types have no built-in row, so the first pick starts one carrying the type.
 *
 * The pick is therefore STORED as the row's visibility — nothing else is written for it, and nothing is deleted.
 * A hide sends `isHidden` with no value, which keeps each cell's provenance (`isVisibilityOnlyWrite`, s223), so
 * the seeded scaffold on a built-in row is not rewritten as the planner's own words by a show/hide.
 *
 * ## What the screen does NOT touch
 *  • `id_number` is Category A — it never reaches the server and is not on this screen (the board agrees);
 *  • the CHILDREN's Government ID rows (`__childgovid__…`) belong to the children screens, and are skipped here.
 */

const IDS = boardStep("ids");
const IDS_TILES = boardTiles(IDS);
const TILES = IDS_TILES.tiles.map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
const WHERE = boardCell(IDS, "where");
export const IDS_TABLE = boardTable(IDS);
const NOTE_FOR = IDS.noteFor ?? {};
const CHILD_ROW_PREFIX = "__childgovid__";

/**
 * Tile id → the STORED `id_type`. The board's tile words and the stored choices differ ("Driver's licence" vs
 * "Driver's License"), so the link is declared here; a test pins every value to a choice the spec offers.
 */
export const ID_TYPE_FOR_TILE: Readonly<Record<string, string>> = {
  sin: "Social Insurance Number (SIN)",
  licence: "Driver's License",
  passport: "Passport",
  health: "Health Card (OHIP)",
  parking: "Accessible parking permit",
  status: "Status card",
};
const TILE_OF_ID_TYPE = new Map(Object.entries(ID_TYPE_FOR_TILE).map(([id, t]) => [t, id]));

/** The section's locked built-in rows, by the `id_type` each one carries — the seeder's own basis. */
export const BUILTIN_ROW_BY_TYPE: Readonly<Record<string, string>> = (() => {
  const section = PERSONAL_INFORMATION.sections[SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS];
  const rows = (section.defaultRows ?? []) as Array<Record<string, string>>;
  const out: Record<string, string> = {};
  rows.filter((r) => !("__v" in r)).forEach((r, i) => {
    if (r._locked === "true" && r.id_type) out[r.id_type] = lockedBuiltInRowId(SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS, i);
  });
  return out;
})();

/** The tile a row belongs to, or null when the row is not one of the six (or is a child's). Pure. */
export function tileOfIdRow(r: Pick<SectionRow, "rowId" | "values">): string | null {
  if (r.rowId.startsWith(CHILD_ROW_PREFIX)) return null;
  return TILE_OF_ID_TYPE.get(r.values[FIELD_KEYS.ID_TYPE] ?? "") ?? null;
}

export function IdsScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return (
    <OnePanelProvider>
      <IdsScreenBody clientId={clientId} onAnswered={onAnswered} />
    </OnePanelProvider>
  );
}

function IdsScreenBody({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const data = useSectionRows(clientId, CATEGORY_SLUGS.PERSONAL_INFORMATION, SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS, onAnswered);
  const { rows } = data;
  const panels = usePanels();
  // Starting a row is Class A (a second fire is a second row) — ONE lock, as on every other screen.
  const createFlight = useSingleFlight();

  /** The row for a tile, whatever its state: the built-in row for the four, else the row carrying that type. */
  const rowOf = useMemo(() => {
    const byTile = new Map<string, SectionRow>();
    for (const r of rows) {
      const tile = tileOfIdRow(r);
      if (tile && !byTile.has(tile)) byTile.set(tile, r);
    }
    return byTile;
  }, [rows]);

  const [selection, setSelection] = useState<TileSelection | null>(null);
  // 🔴 The answer IS the row's visibility (the ruling), so it is seeded from exactly that, and survives a reload.
  useEffect(() => {
    if (selection || !data.ready) return;
    const selected = TILES.map((t) => t.id).filter((id) => { const r = rowOf.get(id); return !!r && !r.hidden; });
    const none = selected.length === 0 && rowOf.size > 0;
    setSelection({ selected, none });
  }, [selection, data.ready, rowOf]);
  const sel = selection ?? { selected: [], none: false };

  const turnOn = async (tileId: string) => {
    const row = rowOf.get(tileId);
    const type = ID_TYPE_FOR_TILE[tileId];
    if (row) {
      if (row.hidden) { await data.setRowHidden(row.rowId, false); data.refresh(); }
      return;
    }
    // No row yet: the four built-in types take their deterministic built-in rowId, so the pick lands on the
    // section's own row (with its _govForms bindings) rather than beside it.
    await createFlight.run(() => data.createRow({ [FIELD_KEYS.ID_TYPE]: type }, BUILTIN_ROW_BY_TYPE[type]));
  };
  const turnOff = async (tileId: string) => {
    const row = rowOf.get(tileId);
    if (!row || row.hidden) return;
    await data.setRowHidden(row.rowId, true);
    data.refresh();
  };

  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) return;
      for (const [, r] of Array.from(rowOf.entries())) if (!r.hidden) await data.setRowHidden(r.rowId, true);
      data.refresh();
      return;
    }
    if (next.selected.includes(changed)) await turnOn(changed);
    else await turnOff(changed);
  };

  /** × is the same answer as un-picking: the row is hidden and its tile goes off. */
  const removeRow = async (rowId: string) => {
    const entry = Array.from(rowOf.entries()).find(([, r]) => r.rowId === rowId);
    if (!entry) return;
    const before = sel;
    setSelection({ selected: sel.selected.filter((id) => id !== entry[0]), none: sel.none });
    try {
      await data.setRowHidden(rowId, true);
    } catch (err) {
      // s228 (ruling 4): a refused × puts the row back — its tile stays picked.
      setSelection(before);
      throw err;
    }
    data.refresh();
  };

  const table: RowTableRow[] = TILES.filter((t) => sel.selected.includes(t.id)).flatMap((t) => {
    const r = rowOf.get(t.id);
    if (!r || r.hidden) return [];
    const v = r.values;
    const tid = (what: string) => `interview-ids-${what}-${t.id}`;
    const whereKey = `${r.rowId}:where`;
    const noteKey = `${r.rowId}:note`;
    const stored = v[FIELD_KEYS.LOCATION] ?? "";
    const write = (val: string) => data.writeCells(r.rowId, { [FIELD_KEYS.LOCATION]: val });
    return [{
      rowId: r.rowId,
      lead: t.label,
      ...(t.mark && t.color ? { leadMark: { mark: t.mark, color: t.color } } : {}),
      cells: [
        <LocationBox key="where" value={stored} placeholder={WHERE.ph} open={panels.isOpen(whereKey)}
          onOpen={() => panels.toggle(whereKey)} onCommit={write} testId={tid("where")} />,
      ],
      // The board's `noDup` covers all six: one row each, so no "Add row".
      canAddRow: false,
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? data.writeCells(r.rowId, { [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      panels: panels.isOpen(whereKey) ? (
        <LocationBoxPanel key={whereKey} value={stored} title={WHERE.bigTitle ?? ""} hint={WHERE.bigHint} placeholder={WHERE.bigPh}
          onCommit={write} onClose={() => panels.close(whereKey)} testId={tid("where")} />
      ) : null,
    }];
  });

  return (
    <div data-testid="interview-ids">
      <TilePicker compact tiles={TILES} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={screenByKey("ids")?.none}
        columns={IDS_TILES.cols as 2 | 3 | 4 | 5} testIdPrefix="interview-ids" />
      <InterviewRowTable rowMarks strongRowLine {...IDS_TABLE} rows={table} onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix="interview-ids-table" cutLead />
      <div className="mt-6 flex flex-col gap-1.5">
        {TILES.filter((t) => sel.selected.includes(t.id) && NOTE_FOR[t.id]).map((t) => (
          <NoteCard key={t.id} mark={t.mark ?? ""} color={t.color ?? ""} testId={`interview-ids-note-${t.id}`}>{NOTE_FOR[t.id]}</NoteCard>
        ))}
      </div>
    </div>
  );
}
